// src/components/SurveyForm.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/SurveyForm.css';
import '../styles/common.css';
import QuestionEditor from './QuestionEditor';
import ChallengeQuestionEditor from './ChallengeQuestionEditor';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useGeo } from '../context/GeoContext';
import LimitedTextarea from './LimitedTextarea';
import { apiGet, apiPost, bootstrapBffSession } from '../lib/apiClient';
import { getRecaptchaToken } from '../lib/recaptcha';
import { useAnonConsent } from '../context/AnonConsentContext';
import SurveyAccessPanel from './SurveyAccessPanel';
import { GripVertical } from 'lucide-react';

const DEFAULT_EXPIRATION_MINUTES = 31 * 24 * 60; // 31 days

const pad = (value) => String(value).padStart(2, '0');

const toDateTimeLocal = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const parseDateTimeLocal = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== 'string') return null;
  const match = value.trim().match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/
  );
  if (!match) return null;
  const [, y, m, d, hh, mm, ss] = match;
  const year = Number(y);
  const month = Number(m) - 1;
  const day = Number(d);
  const hour = Number(hh);
  const minute = Number(mm);
  const second = ss ? Number(ss) : 0;
  const dt = new Date(year, month, day, hour, minute, second, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const computeDefaultExpirationDateTime = () => {
  const dt = new Date();
  dt.setMinutes(dt.getMinutes() + DEFAULT_EXPIRATION_MINUTES);
  dt.setSeconds(0, 0);
  return toDateTimeLocal(dt);
};

const computeMinExpirationDateTime = () => {
  const dt = new Date();
  dt.setSeconds(0, 0);
  return toDateTimeLocal(dt);
};

const isoToDateTimeLocal = (iso) => {
  if (!iso) return '';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '';
  return toDateTimeLocal(dt);
};

const dateTimeLocalToIso = (value) => {
  const dt = parseDateTimeLocal(value);
  if (!dt) return null;
  return dt.toISOString();
};

const formatDateTimeDisplay = (value) => {
  if (!value) return '';
  const dt = parseDateTimeLocal(value) || new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export default function SurveyForm({ postContent = '', reviewText = '', initialSurvey = null }) {
  const [content, setContent] = useState(postContent || reviewText || '');
  const [maxChars, setMaxChars] = useState(1000);
  const [surveyJSON, setSurveyJSON] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [notice, setNotice] = useState('');
  const [isFeedback, setIsFeedback] = useState(false);
  const [challengeQuestions, setChallengeQuestions] = useState([]);

  // Visibility & access (NEW)
  const [access, setAccess] = useState({ ...SurveyAccessPanel.defaults });
  const [expirationDate, setExpirationDate] = useState(() => computeDefaultExpirationDateTime());
  const [embeddedToken, setEmbeddedToken] = useState('');
  const minExpirationDate = computeMinExpirationDateTime();
  const expirationPreview = expirationDate ? formatDateTimeDisplay(expirationDate) : '';

  // legacy fields removed: surveyType, radius
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { coords, status, enableLocation, refreshLocation } = useGeo();
  const { consented } = useAnonConsent();

  // Generate survey via server API (/api/surveys/generate)
  const ensureEmbeddedToken = useCallback(async ({ silent = false } = {}) => {
    if (embeddedToken) return embeddedToken;
    try {
      await bootstrapBffSession();
      const res = await apiPost('/surveys/embedded-token', {});
      const token = res?.embeddedToken || '';
      if (token) {
        setEmbeddedToken(token);
      }
      return token;
    } catch (err) {
      console.error('Failed to fetch embedded token', err);
      if (!silent) {
        alert('Unable to reserve a survey token. Please try again.');
      }
      return '';
    }
  }, [embeddedToken]);

  useEffect(() => {
    bootstrapBffSession().catch(() => {});
  }, []);

  useEffect(() => {
    if (consented) {
      ensureEmbeddedToken({ silent: true }).catch(() => {});
    }
  }, [consented, ensureEmbeddedToken]);

  const generateSurveyFromServer = React.useCallback(async (text) => {
    if (!consented) {
      setNotice('Enable anonymous ID participation on the ID Policy page to use AI generation.');
      return;
    }
    try {
      setGenerating(true);
      await bootstrapBffSession();
      const payload = String(text || '').trim();
      if (payload.length === 0) {
        alert('Please provide some context after @review.');
        return;
      }
      if (payload.length > maxChars) {
        alert(`Please shorten your input to ${maxChars} characters or fewer.`);
        return;
      }
      let recaptchaToken = null;
      try {
        recaptchaToken = await getRecaptchaToken('survey_generate');
      } catch (err) {
        console.error('reCAPTCHA error while generating survey:', err);
        alert(err?.message || 'reCAPTCHA verification failed. Please retry.');
        return;
      }
      const options = recaptchaToken ? { headers: { 'X-Recaptcha-Token': recaptchaToken } } : undefined;
      const data = await apiPost('/surveys/generate', { content: payload, manual: manualMode }, options);
      const normalized = {
        title: data.title || '',
        instructions: data.instructions || '',
        questions: Array.isArray(data.questions)
          ? data.questions.map((q) => ({
              text: q.text || '',
              type: q.type === 'multiple_choice' ? 'multiple_choice' : 'free_text',
              options: q.type === 'multiple_choice' && Array.isArray(q.options) ? q.options : [],
              allowMultiple: !!(q.allow_multiple || q.allowMultiple),
            }))
          : [],
      };
      if (data.notice) {
        setNotice(String(data.notice));
      } else {
        setNotice('');
      }
      setSurveyJSON(normalized);
      setChallengeQuestions([]);
      const token = data.embeddedToken;
      if (token) {
        setEmbeddedToken(token);
      } else {
        await ensureEmbeddedToken();
      }
    } catch (e) {
      const msg = e?.msg || e?.message || 'Failed to generate survey from content.';
      console.error('Generate via server failed', msg);
      alert(msg);
    } finally {
      setGenerating(false);
    }
  }, [maxChars, manualMode, consented, ensureEmbeddedToken]);

  // Ensure server session/cookies exist so user_id is stable and server-derived.
  // Load server-side configured limit
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const h = await apiGet('/health');
        if (!alive) return;
        const v = Number(h?.surveyGenMaxChars);
        if (Number.isFinite(v) && v > 0) {
          setMaxChars(Math.max(1000, v));
        }
      } catch (_) {}
    })();
    return () => { alive = false; };
  }, []);

  // Initial content is seeded from props; user edits locally thereafter.

  // Allow parent to inject a pre-generated survey
  useEffect(() => {
    if (!surveyJSON && initialSurvey) {
      setSurveyJSON(initialSurvey);
      const initialChallenges = Array.isArray(initialSurvey.challengeQuestions)
        ? initialSurvey.challengeQuestions.map((cq) => ({
            text: cq.text || cq.question_text || '',
            expectedAnswer: cq.expectedAnswer || cq.challenge_expected_response || '',
          }))
        : [];
      setChallengeQuestions(initialChallenges);
      const expiresRaw = initialSurvey.expiresAt;
      if (expiresRaw) {
        const parsed = isoToDateTimeLocal(expiresRaw);
        setExpirationDate(parsed || computeDefaultExpirationDateTime());
      }
      const initialVisibility = initialSurvey.visibility || {};
      const existingCode = initialVisibility.code || '';
      const exportCode = initialSurvey.exportHtmlCode || initialSurvey.export_html_code || '';
      setAccess({
        mode: initialVisibility.mode === 'code' ? 'code' : 'global',
        code: existingCode,
        exportHtmlProtected: Boolean(exportCode),
        exportHtmlCode: exportCode || '',
      });
    }
  }, [initialSurvey, surveyJSON]);

  const reorderQuestions = (result) => {
    if (!result.destination) return;
    const items = Array.from(surveyJSON.questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setSurveyJSON({ ...surveyJSON, questions: items });
  };

  const moveQuestion = (index, direction) => {
    const questions = [...surveyJSON.questions];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= questions.length) return;
    [questions[index], questions[targetIndex]] = [questions[targetIndex], questions[index]];
    setSurveyJSON({ ...surveyJSON, questions });
  };

  

  const handleSave = async (statusVal) => {
    if (!surveyJSON) return;
    if (!consented) {
      setNotice('Enable anonymous ID participation on the ID Policy page to create and publish surveys.');
      return;
    }
    const trimmedExportCode = access.exportHtmlProtected ? (access.exportHtmlCode || '').trim() : '';
    if (access.exportHtmlProtected) {
      if (trimmedExportCode.length < 4) {
        alert('Insights export code must be at least 4 characters.');
        return;
      }
      if (/\s/.test(trimmedExportCode)) {
        alert('Insights export code cannot contain spaces.');
        return;
      }
    }
    const normalizedChallenges = challengeQuestions.map((q) => ({
      text: (q.text || '').trim(),
      expectedAnswer: (q.expectedAnswer || '').trim(),
    }));
    const hasPartialChallenge = normalizedChallenges.some(
      (q) => (q.text && !q.expectedAnswer) || (!q.text && q.expectedAnswer)
    );
    if (hasPartialChallenge) {
      alert('Each challenge question must include both a question and an expected answer.');
      return;
    }
    const filteredChallenges = normalizedChallenges.filter((q) => q.text && q.expectedAnswer);
    try {
      setLoading(true);
      await bootstrapBffSession(); // ensure stable server identity

      // Build visibility (MVP): 'global' or 'code'
      const visibilityMode = access.mode === 'code' ? 'code' : 'global';

      // No location/device logic in MVP visibility

      // Keep compatibility with existing BFF: send legacy fields + new visibility
      const radiusMeters = null;

      const regularQuestions = (surveyJSON.questions || []).map((q) => ({
        text: q.text || '',
        type: q.type === 'multiple_choice' ? 'multiple_choice' : 'free_text',
        options: q.type === 'multiple_choice' ? (q.options || []) : [],
        allowMultiple: q.type === 'multiple_choice' ? !!q.allowMultiple : undefined,
        isChallenge: false,
      }));

      const challengePayload = filteredChallenges.map((q) => ({
        text: q.text,
        type: 'free_text',
        options: [],
        isChallenge: true,
        challengeExpectedResponse: q.expectedAnswer,
      }));

      const normalizedExpiration = expirationDate || computeDefaultExpirationDateTime();
      if (!expirationDate) {
        setExpirationDate(normalizedExpiration);
      }
      const expiresAtIso = dateTimeLocalToIso(normalizedExpiration);

      const surveyPayload = {
        title: surveyJSON.title,
        instructions: surveyJSON.instructions,
        type: 'geo',
        feedback: !!isFeedback,
        radiusMeters,
        lat: null,
        lng: null,
        visibility: {
          mode: visibilityMode,
          code: visibilityMode === 'code' ? access.code : null,
        },
        questions: [...regularQuestions, ...challengePayload],
      };
      if (access.exportHtmlProtected) {
        surveyPayload.exportHtmlCode = trimmedExportCode;
      }
      if (expiresAtIso) {
        surveyPayload.expiresAt = expiresAtIso;
      }

      const tokenValue = await ensureEmbeddedToken();
      if (!tokenValue) {
        setLoading(false);
        return;
      }
      surveyPayload.embeddedToken = tokenValue;

      const payload = { survey: surveyPayload };

      // Prefer your existing endpoint to avoid breaking server; it can ignore unknown fields
      let recaptchaToken = null;
      try {
        recaptchaToken = await getRecaptchaToken('survey_create');
      } catch (err) {
        console.error('reCAPTCHA error while creating survey:', err);
        alert(err?.message || 'reCAPTCHA verification failed. Please retry.');
        return;
      }

      const options = recaptchaToken ? { headers: { 'X-Recaptcha-Token': recaptchaToken } } : undefined;
      const res = await apiPost('/surveys', payload, options);
      const newId = res && (res.id || res.surveyId);
      navigate('/', { state: { createdSurveyId: newId } });
    } catch (e) {
      console.error(e);
      const message = e?.msg || e?.message;
      if (message) {
        alert(message);
      } else {
        alert('Unexpected error while saving survey.');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateQuestion = (index, updated) => {
    const questions = [...surveyJSON.questions];
    questions[index] = updated;
    setSurveyJSON({ ...surveyJSON, questions });
  };

  const deleteQuestion = (index) => {
    const questions = surveyJSON.questions.filter((_, i) => i !== index);
    setSurveyJSON({ ...surveyJSON, questions });
  };

  const addQuestion = (kind = 'free_text') => {
    const newQ = {
      text: '',
      type: kind === 'multiple_choice' ? 'multiple_choice' : 'free_text',
      options: kind === 'multiple_choice' ? [''] : [],
      allowMultiple: kind === 'multiple_choice' ? false : undefined,
    };
    setSurveyJSON({ ...surveyJSON, questions: [...surveyJSON.questions, newQ] });
  };

  const addChallengeQuestion = () => {
    setChallengeQuestions((prev) => [
      ...prev,
      {
        text: '',
        expectedAnswer: '',
      },
    ]);
  };

  const updateChallengeQuestion = (index, updated) => {
    setChallengeQuestions((prev) => {
      const next = [...prev];
      next[index] = updated;
      return next;
    });
  };

  const deleteChallengeQuestion = (index) => {
    setChallengeQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const moveChallengeQuestion = (index, direction) => {
    setChallengeQuestions((prev) => {
      const next = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= next.length) return prev;
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  };

  const setQuestionType = (index, type) => {
    const q = surveyJSON.questions[index];
    const next = { ...q, type };
    if (type === 'multiple_choice' && (!Array.isArray(next.options) || next.options.length === 0)) {
      next.options = [''];
      if (typeof next.allowMultiple === 'undefined') next.allowMultiple = false;
    }
    if (type === 'free_text') {
      next.options = [];
      delete next.allowMultiple;
    }
    updateQuestion(index, next);
  };

  const addOptionToQuestion = (index) => {
    const q = surveyJSON.questions[index];
    const nextOptions = Array.isArray(q.options) ? [...q.options] : [];
    nextOptions.push('');
    updateQuestion(index, { ...q, options: nextOptions });
  };

  if (!surveyJSON) {
    return (
      <form className="survey-form">
        {notice && (
          <div className="card" style={{ background: '#fff8e1', border: '1px solid #facc15', color: '#92400e', padding: '8px 12px', borderRadius: 8, marginBottom: 8 }}>
            {notice}
          </div>
        )}
        {!consented && (
          <div className="muted" style={{ marginBottom: 8 }}>
            Read-only mode: visit the <a href="/privacy/anonymous-id">ID Policy</a> page to enable participation.
          </div>
        )}
        <fieldset disabled={!consented} style={{ border: 'none', padding: 0, margin: 0 }}>
          <textarea
            className="survey-textarea"
            placeholder={
              'Describe what you want your survey to cover, or direct AI with keywords like "create survey exactly with these questions" and quote them. Mention a specific language if you want the survey generated in it.'
            }
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={6}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
            <small className={content.length > maxChars ? 'muted error' : 'muted'}>
              {content.length}/{maxChars}
            </small>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }} title="Skip AI and start from a blank template">
              <input type="checkbox" checked={manualMode} onChange={(e) => setManualMode(e.target.checked)} />
              Start from scratch
            </label>
          </div>

          <button
            type="submit"
            className="post-button"
            onClick={(e) => {
              e.preventDefault();
              generateSurveyFromServer(content);
            }}
            disabled={generating || !consented}
            style={{ marginTop: 12, position: 'relative' }}
          >
            {generating ? 'Generating…' : manualMode ? (consented ? 'Start Manual Draft' : 'Enable participation to draft') : (consented ? 'Generate Survey' : 'Enable participation to generate')}
          </button>

          {!manualMode && (
            <div className="muted" style={{ marginTop: 6 }}>
              AI drafting usually finishes within tens of seconds during beta.
            </div>
          )}

          {generating && (
            <div className="progress-bar" aria-label="Generating survey, please wait" role="status" style={{ marginTop: 12 }}>
              <div className="progress-bar-inner" />
            </div>
          )}
        </fieldset>
      </form>
    );
  }

  return (
    <div className="survey-form">
      {notice && (
        <div className="card" style={{ background: '#fff8e1', border: '1px solid #facc15', color: '#92400e', padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}>
          {notice}
        </div>
      )}
      {!consented && (
        <div className="muted" style={{ marginBottom: 12 }}>
          Read-only mode: enable anonymous ID participation on the <a href="/privacy/anonymous-id">ID Policy</a> page to edit and publish surveys.
        </div>
      )}
      <fieldset disabled={!consented} style={{ border: 'none', padding: 0, margin: 0 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12 }}>
        <div>
          <label>
            <input
              type="checkbox"
              checked={previewMode}
              onChange={(e) => setPreviewMode(e.target.checked)}
            />{' '}
            Preview Mode
          </label>
        </div>
        <p className="muted" style={{ margin: 0 }}>
          Rearrange questions with drag-and-drop or the up/down buttons, edit any text inline, and remove or add prompts with the controls below.
        </p>
      </div>

      {!previewMode ? (
        <>
          <label className="field-label" htmlFor="survey-title" title="Title is searchable">Survey Title</label>
          <div id="survey-title-help" className="help-text">Searchable: included in search results. Use clear keywords.</div>
          <LimitedTextarea
            id="survey-title"
            value={surveyJSON.title}
            onChange={(e) => setSurveyJSON({ ...surveyJSON, title: e.target.value })}
            className="survey-textarea survey-title"
            rows={2}
            max={240}
            placeholder="Enter a clear, keyword-rich title (searchable)"
            title="This title is searchable — use clear keywords"
            aria-describedby="survey-title-help"
          />

          <label className="field-label" htmlFor="survey-instructions" title="Instructions are searchable">Survey Instructions</label>
          <div id="survey-instructions-help" className="help-text">Searchable: helps people discover your survey.</div>
          <LimitedTextarea
            id="survey-instructions"
            value={surveyJSON.instructions}
            onChange={(e) => setSurveyJSON({ ...surveyJSON, instructions: e.target.value })}
            className="survey-textarea"
            rows={3}
            max={1000}
            placeholder="Add brief instructions to guide responders (searchable)"
            title="These instructions are searchable — keep them concise and relevant"
            aria-describedby="survey-instructions-help"
          />

          <label className="field-label" htmlFor="survey-expiration" title="Set a response deadline">
            Responses Close On
          </label>
          <div id="survey-expiration-help" className="help-text">
            The default response deadline is 31 days from today. Surveys and their associated data are automatically deleted 31 days after creation, even if you select a later response deadline.
          </div>
          <input
            id="survey-expiration"
            type="datetime-local"
            value={expirationDate}
            min={minExpirationDate}
            step={60}
            onChange={(e) => setExpirationDate(e.target.value)}
            aria-describedby="survey-expiration-help"
            style={{ maxWidth: 260, padding: '8px', borderRadius: 8, border: '1px solid #d1d5db' }}
          />
          <div className="muted" style={{ marginTop: 4 }}>
            {expirationPreview
              ? `Responses close on ${expirationPreview}.`
              : 'Select when responses should close.'}
          </div>

          <div className="help-text" id="questions-help" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
            <GripVertical size={14} aria-hidden="true" />
            <span>Tip: drag questions to reorder (or use Up/Down).</span>
          </div>

          {/* Feedback classification (show on Feedback page) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <input
              type="checkbox"
              id="is-feedback"
              checked={isFeedback}
              onChange={(e) => setIsFeedback(e.target.checked)}
            />
            <label
              htmlFor="is-feedback"
              title="If checked, this survey appears on the Feedback page for others to vote and comment."
              style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
            >
              List on Feedback page (getSurvey feedback/requests)
            </label>
          </div>

          <DragDropContext onDragEnd={reorderQuestions}>
            <Droppable droppableId="questions">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {surveyJSON.questions.map((q, index) => (
                    <Draggable key={`q-${index}`} draggableId={`q-${index}`} index={index}>
                      {(dragProvided) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className="question-block"
                          title="Drag to reorder question"
                        >
                          <QuestionEditor
                            question={q}
                            index={index}
                            onUpdate={(i, updated) => updateQuestion(i, updated)}
                            onDelete={deleteQuestion}
                            onMove={moveQuestion}
                            onTypeChange={(type) => setQuestionType(index, type)}
                            onAddOption={() => addOptionToQuestion(index)}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <div className="card" style={{ marginTop: 16, padding: '16px', borderRadius: 12 }}>
            <h3 style={{ marginTop: 0 }}>New Question</h3>
            <div className="question-buttons" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
              <button type="button" className="post-button" onClick={() => addQuestion('free_text')}>
                + Add Free Text
              </button>
              <button type="button" className="post-button" onClick={() => addQuestion('multiple_choice')}>
                + Add Multiple Choice
              </button>
            </div>
          </div>

          <div className="card" style={{ marginTop: 16, padding: '16px', borderRadius: 12 }}>
            <h3 style={{ marginTop: 0 }}>Access &amp; Visibility</h3>
            <p className="muted" style={{ marginTop: 4 }}>
              Decide who can open the survey before you publish it.
            </p>
            <SurveyAccessPanel value={access} onChange={setAccess} />
          </div>

          <div className="card" style={{ marginTop: 16 }}>
            <h3 style={{ marginTop: 0 }}>Challenge Questions (optional)</h3>
            <p className="muted" style={{ marginTop: 4 }}>
              Challenge questions filter out respondents who lack the right context. They are ideal for
              company-only surveys, project retros, or team feedback where you want proof the responder is involved.
              They are optional—skip them for open public feedback.
              <a
                href="/guide#challenge-questions"
                style={{ marginLeft: 6 }}
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn how challenge questions work
              </a>.
            </p>
            {challengeQuestions.length === 0 && (
              <p className="muted" style={{ marginTop: 12 }}>
                No challenge questions yet.
              </p>
            )}
            {challengeQuestions.map((q, index) => (
              <div key={`challenge-${index}`} className="question-block" style={{ marginTop: 12 }}>
                <ChallengeQuestionEditor
                  question={q}
                  index={index}
                  onUpdate={updateChallengeQuestion}
                  onDelete={deleteChallengeQuestion}
                  onMove={moveChallengeQuestion}
                />
              </div>
            ))}
            <button type="button" className="post-button" style={{ marginTop: 12 }} onClick={addChallengeQuestion}>
              + Add Challenge Question
            </button>
          </div>

          <div className="form-actions">
            <button onClick={() => handleSave('published')} className="post-button" disabled={loading}>
              Publish
            </button>
          </div>
        </>
      ) : (
        <>
          <h3>{surveyJSON.title}</h3>
          <p>{surveyJSON.instructions}</p>
          <ul>
            {surveyJSON.questions.map((q, i) => (
              <li key={i}>
                <strong>{q.text}</strong>
                {q.type === 'multiple_choice' ? (
                  <ul>{(q.options || []).map((o, j) => <li key={j}>{o}</li>)}</ul>
                ) : (
                  <p><em>(Free text response)</em></p>
                )}
              </li>
            ))}
          </ul>
          {challengeQuestions.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4>Challenge Questions</h4>
              <ul>
                {challengeQuestions.map((q, idx) => (
                  <li key={`preview-challenge-${idx}`}>
                    <strong>{q.text || '(Untitled challenge question)'}</strong>
                    <div className="muted" style={{ marginTop: 4 }}>
                      Expected answer: {q.expectedAnswer || '(not set)'}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      </fieldset>
    </div>
  );
}
